import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Check, ChevronDown, X } from 'lucide-react';
import { createPortal } from 'react-dom';

import { useTagDefinitions } from '@/hooks/useTagDefinitions';
import { cn } from '@/lib/utils';

const AUTO_CLOSE_MS = 5000;

/**
 * Compact "Tags" trigger for the narrow list pane. Opens a portal panel with
 * one column per tag type (plus "other" for untyped/legacy tags); tags are
 * multi-selectable — OR within a type, AND across types (the backend groups
 * them the same way). Closes on outside click, Escape, the close button, or
 * 5s after the last interaction.
 */
export const TagFilterPopover = ({
  selected,
  onChange,
  extraTags = [],
}: {
  selected: string[];
  onChange: (tags: string[]) => void;
  /** Tags in use that aren't in the catalog (legacy/untyped). */
  extraTags?: string[];
}) => {
  const { definitions, byName } = useTagDefinitions();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  const columns = useMemo(() => {
    const byType = new Map<string, string[]>();
    const catalogNames = new Set<string>();
    for (const def of definitions) {
      catalogNames.add(def.name);
      const type = def.type?.trim();
      if (!type) continue;
      byType.set(type, [...(byType.get(type) ?? []), def.name]);
    }
    const untyped = Array.from(
      new Set([
        ...definitions.filter((d) => !d.type?.trim()).map((d) => d.name),
        ...extraTags.filter((t) => !catalogNames.has(t)),
      ]),
    );
    const cols: [string, string[]][] = [...byType.entries()];
    if (untyped.length > 0) cols.push(['other', untyped]);
    return cols;
  }, [definitions, extraTags]);

  const toggle = (tag: string) =>
    onChange(selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag]);

  // Auto-close a while after the last interaction inside the panel.
  const armCloseTimer = useCallback(() => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => setOpen(false), AUTO_CLOSE_MS);
  }, []);

  useEffect(() => {
    if (!open) return;
    armCloseTimer();
    const onDown = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, [open, armCloseTimer]);

  // The panel is wider than the 280px pane, so position it in the viewport.
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.min(Math.max(columns.length * 150 + 24, 300), window.innerWidth - 16);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
    setPos({ top: rect.bottom + 6, left: Math.max(8, left), width });
  }, [open, columns.length]);

  if (columns.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Filter by tags"
        className={cn(
          'flex h-7 min-w-0 flex-1 cursor-pointer items-center justify-between gap-1 rounded-md border-0 bg-white px-2 text-xs focus:ring-1 focus:ring-[#00a884] focus:outline-none dark:bg-[#2a3942]',
          selected.length > 0 ? 'text-[#00a884]' : 'text-[#667781] dark:text-[#8696a0]',
        )}
      >
        <span className="truncate">
          {selected.length > 0 ? `Tags: ${selected.length}` : 'Tags: any'}
        </span>
        <ChevronDown className="h-3 w-3 shrink-0" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            onPointerMove={armCloseTimer}
            onClick={armCloseTimer}
            className="fixed z-[9999] rounded-lg border border-[#d1d7db] bg-white shadow-xl dark:border-[#2a3942] dark:bg-[#1a2730]"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            <div className="flex items-center justify-between border-b border-[#e4e6e9] px-3 py-2 dark:border-[#2a3942]">
              <span className="text-xs font-semibold text-[#111b21] dark:text-[#e9edef]">
                Filter by tags
              </span>
              <div className="flex items-center gap-2">
                {selected.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="cursor-pointer text-[11px] text-[#667781] hover:text-[#00a884] dark:text-[#8696a0]"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  title="Close"
                  className="cursor-pointer text-[#667781] transition-colors hover:text-[#111b21] dark:text-[#8696a0] dark:hover:text-[#e9edef]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto p-2.5">
              {columns.map(([type, tags]) => (
                <div key={type} className="min-w-[120px] flex-1 space-y-0.5">
                  <p className="px-2 pb-1 text-[10px] font-semibold tracking-wide text-[#667781] uppercase dark:text-[#8696a0]">
                    {type}
                  </p>
                  {tags.map((tag) => {
                    const def = byName.get(tag);
                    const color = def?.color || '#6b7b8d';
                    const active = selected.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggle(tag)}
                        className={cn(
                          'flex w-full cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs transition-colors',
                          !active &&
                            'text-[#3b4a54] hover:bg-[#f0f2f5] dark:text-[#d1d7db] dark:hover:bg-[#2a3942]',
                        )}
                        style={active ? { backgroundColor: `${color}26`, color } : undefined}
                      >
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        <span className="min-w-0 flex-1 truncate">{tag}</span>
                        {active && <Check className="h-3 w-3 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
